import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonFilters, ButtonVariants, FilterType, IconNames } from '@triumph/design-system';
import useExportToFile from '@/app/shared/utils/useExportToFile';
import {
  TotalItemsCount,
  TotalItemsRow,
  TotalItemsLeftSection,
  TotalItemsRightSection,
  ButtonFiltersWrapper,
  FiltersContainer,
  DateRangeLabel,
} from '@/app/features/DebtorPortal/Dashboard/InvoicesTable/Invoices.StyledComponents';
import { BillingReminder } from '../billingReminder.types';
import { FileType } from '@/app/shared/constants/misc/FileType';
import { BillingReminderFilterOptions } from '@/app/shared/hooks/useBillingRemindersTable';

interface AllInvoicesTableFiltersProps {
  rows: BillingReminder[];
  totalInvoices: number;
  onButtonFilterChange: (column: string, value: string) => Promise<void>;
  availableFilterOptions: BillingReminderFilterOptions;
  dateRangeDisplay: string | null;
}

export const AllInvoicesTableFilters = ({
  rows,
  totalInvoices,
  onButtonFilterChange,
  availableFilterOptions,
  dateRangeDisplay,
}: AllInvoicesTableFiltersProps) => {
  const { t } = useTranslation('billingReminder');
  const { exportFile } = useExportToFile();

  const carrierLabel = t('table.carrierName');
  const statusLabel = t('table.status');
  const ageLabel = t('table.age');
  const filterByAgeHeader = t('filters.filterByAge');

  const { carrierOptions, statusOptions, ageOptions } = useMemo(() => ({
    carrierOptions: availableFilterOptions.carrierOptions.map((v) => ({ value: v, originalValue: v })),
    statusOptions: availableFilterOptions.statusOptions.map((v) => ({ value: v, originalValue: v })),
    ageOptions: availableFilterOptions.ageOptions.map((v) => ({ value: v, originalValue: v })),
  }), [availableFilterOptions]);

  const filterConfigs = useMemo(() => [
    {
      id: 'carrierFilter',
      column: 'carrierName',
      filterType: FilterType.Button,
      label: carrierLabel,
      data: [],
      getData: async (term?: string | null) =>
        term ? carrierOptions.filter((o) => o.value.toLowerCase().includes(term.toLowerCase())) : carrierOptions,
      disableSearch: false,
      title: carrierLabel,
      header: carrierLabel,
    },
    {
      id: 'statusFilter',
      column: 'status',
      filterType: FilterType.Button,
      label: statusLabel,
      data: [],
      getData: async (term?: string | null) =>
        term ? statusOptions.filter((o) => o.value.toLowerCase().includes(term.toLowerCase())) : statusOptions,
      disableSearch: false,
      title: statusLabel,
      header: statusLabel,
    },
    {
      id: 'ageFilter',
      column: 'age',
      filterType: FilterType.Button,
      label: ageLabel,
      data: [],
      getData: async (term?: string | null) =>
        term ? ageOptions.filter((o) => o.value.toLowerCase().includes(term.toLowerCase())) : ageOptions,
      disableSearch: true,
      title: ageLabel,
      header: filterByAgeHeader,
    },
  ], [carrierOptions, statusOptions, ageOptions, carrierLabel, statusLabel, ageLabel, filterByAgeHeader]);

  const handleExport = () => {
    const headers = [
      t('table.invoiceNumber'), t('table.referenceNumber'), t('table.carrierName'),
      t('table.amount'), t('table.invoiceDate'), t('table.invoiceDueDate'),
      t('table.age'), t('table.status'),
    ];
    const csvRows = rows.map((r) =>
      [r.invoiceNumber, r.referenceNumber, r.carrierName, r.amount, r.invoiceDate, r.invoiceDueDate, r.age ?? '', r.status ?? '']
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [headers.join(','), ...csvRows].join('\n');
    exportFile('BillingReminder', new Blob([csv], { type: 'text/csv' }), FileType.Csv);
  };

  return (
    <FiltersContainer>
      <ButtonFiltersWrapper>
        <ButtonFilters
          id="allInvoicesFilters"
          filters={filterConfigs}
          onButtonFilterSearch={onButtonFilterChange}
          hiddenColumns={[]}
          hideResetFilter={true}
          preSelectedFilters={[]}
        />
      </ButtonFiltersWrapper>
      <TotalItemsRow>
        <TotalItemsLeftSection>
          <TotalItemsCount>{totalInvoices.toLocaleString()} {t('table.items')}</TotalItemsCount>
          {dateRangeDisplay && <DateRangeLabel>{dateRangeDisplay}</DateRangeLabel>}
        </TotalItemsLeftSection>
        <TotalItemsRightSection>
          <Button
            variant={ButtonVariants.tertiary}
            icon={IconNames.Interface_Download}
            iconLeft={true}
            onClick={handleExport}
          >
            {t('table.exportList')}
          </Button>
        </TotalItemsRightSection>
      </TotalItemsRow>
    </FiltersContainer>
  );
};
